import { createTheme, ThemeOptions } from "@mui/material/styles";
import { colors } from "./colors";

const baseTheme: ThemeOptions = {
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13,
    h1: {
      fontSize: "2rem",
    },
    h2: {
      fontSize: "1.75rem",
    },
    h3: {
      fontSize: "1.5rem",
    },
    h4: {
      fontSize: "1.25rem",
    },
    h5: {
      fontSize: "1.1rem",
    },
    h6: {
      fontSize: "1rem",
    },
    body1: {
      fontSize: "0.875rem",
    },
    body2: {
      fontSize: "0.8125rem",
    },
    button: {
      fontSize: "0.8125rem",
      textTransform: "none" as const,
    },
    caption: {
      fontSize: "0.75rem",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        body {
          background-color: ${colors.dark.background.default};
          color: ${colors.dark.text.primary};
        }
        body[data-theme="light"] {
          background-color: ${colors.light.background.default};
          color: ${colors.light.text.primary};
        }
      `,
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "light"
              ? colors.light.background.sidebar
              : colors.dark.background.sidebar,
        }),
      },
    },
    MuiTable: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "light"
              ? colors.light.background.table
              : colors.dark.background.table,
        }),
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "light"
              ? colors.light.background.table
              : colors.dark.background.table,
        }),
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "light"
              ? colors.light.background.paper
              : colors.dark.background.paper,
          "& .MuiTableCell-head": {
            color:
              theme.palette.mode === "light"
                ? colors.light.text.primary
                : colors.dark.text.primary,
            fontWeight: 600,
          },
        }),
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "light"
              ? colors.light.background.table
              : colors.dark.background.table,
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&:nth-of-type(odd)": {
            backgroundColor:
              theme.palette.mode === "light"
                ? colors.light.background.default
                : colors.dark.background.default,
          },
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "light"
                ? colors.light.background.hover
                : colors.dark.background.hover + " !important",
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor:
            theme.palette.mode === "light"
              ? colors.light.border.default
              : colors.dark.border.default,
          color:
            theme.palette.mode === "light"
              ? colors.light.text.primary
              : colors.dark.text.primary,
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 4,
          padding: "6px 16px",
          height: "36px",
          minWidth: "64px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          },
          ...(theme.palette.mode === "dark" && {
            color: colors.dark.text.primary,
          }),
        }),
        containedPrimary: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "light"
              ? colors.light.brand.primary
              : colors.dark.brand.primary,
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "light"
                ? colors.light.brand.primaryDark
                : colors.dark.brand.primaryDark,
          },
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
          backgroundColor:
            theme.palette.mode === "light"
              ? "rgba(255, 255, 255, 0.9)"
              : "rgba(33, 33, 33, 0.9)",
          "& .MuiTypography-root": {
            color:
              theme.palette.mode === "light"
                ? colors.light.text.primary
                : colors.dark.text.primary,
          },
          "& .MuiIconButton-root": {
            color:
              theme.palette.mode === "light"
                ? colors.light.text.primary
                : colors.dark.text.primary,
          },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "light"
              ? "rgba(255, 255, 255, 0.9)"
              : "rgba(33, 33, 33, 0.9)",
          color:
            theme.palette.mode === "light"
              ? colors.light.text.primary
              : colors.dark.text.primary,
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "light"
                ? "rgba(255, 255, 255, 0.8)"
                : "rgba(33, 33, 33, 0.8)",
          },
        }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: "0.8125rem",
        },
        secondary: {
          fontSize: "0.75rem",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "light"
                ? colors.light.background.hover
                : colors.dark.background.hover,
          },
          "&.Mui-selected": {
            backgroundColor:
              theme.palette.mode === "light"
                ? colors.light.background.selected
                : colors.dark.background.selected,
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "light"
                  ? colors.light.background.selectedHover
                  : colors.dark.background.selectedHover,
            },
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "light"
              ? colors.light.background.paper
              : colors.dark.background.paper,
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& .MuiOutlinedInput-root": {
            backgroundColor:
              theme.palette.mode === "light"
                ? colors.light.background.paper
                : colors.dark.background.paper,
            "& fieldset": {
              borderColor:
                theme.palette.mode === "light"
                  ? colors.light.border.default
                  : colors.dark.border.default,
            },
            "&:hover fieldset": {
              borderColor:
                theme.palette.mode === "light"
                  ? colors.light.brand.primary
                  : colors.dark.brand.primary,
            },
            "&.Mui-focused fieldset": {
              borderColor:
                theme.palette.mode === "light"
                  ? colors.light.brand.primary
                  : colors.dark.brand.primary,
            },
            "& input": {
              color:
                theme.palette.mode === "light"
                  ? colors.light.text.primary
                  : colors.dark.text.primary,
            },
            "&.Mui-focused": {
              backgroundColor:
                theme.palette.mode === "light"
                  ? colors.light.background.paper
                  : colors.dark.background.paper,
            },
            "&::selection": {
              backgroundColor:
                theme.palette.mode === "light"
                  ? `${colors.light.brand.primary}40`
                  : `${colors.dark.brand.primary}40`,
            },
          },
          "& .MuiInputLabel-root": {
            color:
              theme.palette.mode === "light"
                ? colors.light.text.secondary
                : colors.dark.text.secondary,
            "&.Mui-focused": {
              color:
                theme.palette.mode === "light"
                  ? colors.light.brand.primary
                  : colors.dark.brand.primary,
            },
          },
          "& .MuiFormHelperText-root": {
            color:
              theme.palette.mode === "light"
                ? colors.light.text.secondary
                : colors.dark.text.secondary,
            "&.Mui-error": {
              color:
                theme.palette.mode === "light"
                  ? colors.light.status.error
                  : colors.dark.status.error,
            },
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 4,
          height: "24px",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          }),
          "&.MuiChip-colorSuccess": {
            backgroundColor:
              theme.palette.mode === "light"
                ? colors.light.status.success
                : "rgba(102, 187, 106, 0.15)",
            color:
              theme.palette.mode === "light"
                ? "#fff"
                : colors.light.status.success,
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(102, 187, 106, 0.35)"
                : undefined,
          },
          "&.MuiChip-colorWarning": {
            backgroundColor:
              theme.palette.mode === "light"
                ? colors.light.status.warning
                : "rgba(255, 167, 38, 0.18)",
            color:
              theme.palette.mode === "light"
                ? "#fff"
                : colors.light.status.warning,
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 167, 38, 0.4)"
                : undefined,
          },
          "&.MuiChip-colorError": {
            backgroundColor:
              theme.palette.mode === "light"
                ? colors.light.status.error
                : "rgba(239, 83, 80, 0.18)",
            color:
              theme.palette.mode === "light"
                ? "#fff"
                : colors.light.status.error,
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(239, 83, 80, 0.4)"
                : undefined,
          },
          "&.MuiChip-colorInfo": {
            backgroundColor:
              theme.palette.mode === "light"
                ? colors.light.status.info
                : "rgba(41, 182, 246, 0.18)",
            color:
              theme.palette.mode === "light"
                ? "#fff"
                : colors.light.status.info,
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(41, 182, 246, 0.4)"
                : undefined,
          },
          "&.MuiChip-colorDefault": {
            backgroundColor:
              theme.palette.mode === "light"
                ? colors.light.background.default
                : "rgba(158, 158, 158, 0.15)",
            color:
              theme.palette.mode === "light"
                ? colors.light.text.primary
                : colors.dark.text.primary,
          },
        }),
        label: {
          fontSize: "0.75rem",
          padding: "0 8px",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "light"
              ? colors.light.background.paper
              : colors.dark.background.paper,
          color:
            theme.palette.mode === "light"
              ? colors.light.text.primary
              : colors.dark.text.primary,
        }),
        standardError: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "light"
              ? `${colors.light.status.error}15`
              : `${colors.dark.status.error}15`,
          "& .MuiAlert-icon": {
            color:
              theme.palette.mode === "light"
                ? colors.light.status.error
                : colors.dark.status.error,
          },
        }),
        standardSuccess: ({ theme }) => ({
          "& .MuiAlert-icon": {
            color:
              theme.palette.mode === "light"
                ? colors.light.status.success
                : colors.dark.status.success,
          },
        }),
        standardWarning: ({ theme }) => ({
          "& .MuiAlert-icon": {
            color:
              theme.palette.mode === "light"
                ? colors.light.status.warning
                : colors.dark.status.warning,
          },
        }),
        standardInfo: ({ theme }) => ({
          "& .MuiAlert-icon": {
            color:
              theme.palette.mode === "light"
                ? colors.light.status.info
                : colors.dark.status.info,
          },
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        h5: ({ theme }) => ({
          color:
            theme.palette.mode === "light"
              ? colors.light.text.primary
              : colors.dark.text.primary,
          fontWeight: 600,
          fontSize: "1.1rem",
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          color:
            theme.palette.mode === "light"
              ? colors.light.text.primary
              : colors.dark.text.primary,
          fontSize: "0.875rem",
          fontWeight: 600,
          borderBottom: "1px solid",
          borderColor: "divider",
          paddingBottom: "16px",
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
          color: theme.palette.mode === "dark" ? "#ffffff" : "#1a1a1a",
          border: `1px solid ${
            theme.palette.mode === "dark" ? "#333333" : "#e0e0e0"
          }`,
          borderRadius: "4px",
          padding: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          fontSize: "0.75rem",
          "& .MuiTypography-caption": {
            fontSize: "0.75rem",
            display: "block",
            "&[color='error']": {
              color: theme.palette.error.main,
            },
            "&[color='warning.main']": {
              color: theme.palette.warning.main,
            },
            "&[color='text.secondary']": {
              color: theme.palette.text.secondary,
            },
          },
        }),
        arrow: ({ theme }) => ({
          color: theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
          "&::before": {
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#333333" : "#e0e0e0"
            }`,
            backgroundColor: "inherit",
          },
        }),
      },
    },
  },
};

const lightPalette: ThemeOptions["palette"] = {
  mode: "light",
  primary: {
    main: colors.light.brand.primary,
    light: colors.light.brand.primaryLight,
    dark: colors.light.brand.primaryDark,
  },
  secondary: {
    main: colors.light.brand.secondary,
  },
  background: {
    default: colors.light.background.default,
    paper: colors.light.background.paper,
  },
  text: {
    primary: colors.light.text.primary,
    secondary: colors.light.text.secondary,
  },
  error: {
    main: colors.light.status.error,
  },
  warning: {
    main: colors.light.status.warning,
  },
  info: {
    main: colors.light.status.info,
  },
  success: {
    main: colors.light.status.success,
  },
  divider: colors.light.border.divider,
};

const darkPalette: ThemeOptions["palette"] = {
  mode: "dark",
  primary: {
    main: colors.dark.brand.primary,
    light: colors.dark.brand.primaryLight,
    dark: colors.dark.brand.primaryDark,
  },
  secondary: {
    main: colors.dark.brand.secondary,
  },
  background: {
    default: colors.dark.background.default,
    paper: colors.dark.background.paper,
  },
  text: {
    primary: colors.dark.text.primary,
    secondary: colors.dark.text.secondary,
  },
  error: {
    main: colors.dark.status.error,
  },
  warning: {
    main: colors.dark.status.warning,
  },
  info: {
    main: colors.dark.status.info,
  },
  success: {
    main: colors.dark.status.success,
  },
  divider: colors.dark.border.divider,
};

export const lightTheme = createTheme({
  ...baseTheme,
  palette: lightPalette,
});

export const darkTheme = createTheme({
  ...baseTheme,
  palette: darkPalette,
});
